export function normalizeStartTimeRanges(rawRanges) {
  if (!Array.isArray(rawRanges)) return [];
  return rawRanges.map((range) => ({
    rangeStart: Number(range?.range_start ?? range?.rangeStart ?? 0),
    rangeEnd: Number(range?.range_end ?? range?.rangeEnd ?? 0),
    probabilityWeightPct: Number(
      range?.probabilityWeightPct ??
      range?.probability_weight_pct ??
      (Number(range?.prob_weight ?? 0) * 100)
    ),
  }));
}

export function validateStartTimeRanges(rawRanges, tolerance = 0.000001) {
  const ranges = normalizeStartTimeRanges(rawRanges);
  if (!ranges.length) return { valid: false, totalPct: 0, error: "Add at least one departure range." };

  for (let index = 0; index < ranges.length; index += 1) {
    const range = ranges[index];
    if (![range.rangeStart, range.rangeEnd, range.probabilityWeightPct].every(Number.isFinite)) {
      return { valid: false, totalPct: NaN, error: `Range ${index + 1} requires numeric values.` };
    }
    if (!Number.isInteger(range.rangeStart) || !Number.isInteger(range.rangeEnd)) {
      return { valid: false, totalPct: NaN, error: `Range ${index + 1} requires whole minutes.` };
    }
    if (range.rangeStart < 0 || range.rangeEnd <= range.rangeStart) {
      return { valid: false, totalPct: NaN, error: `Range ${index + 1} must have an end greater than its non-negative start.` };
    }
    if (range.probabilityWeightPct <= 0 || range.probabilityWeightPct > 100) {
      return { valid: false, totalPct: NaN, error: `Range ${index + 1} requires a population percentage above 0 and at most 100.` };
    }
  }

  const sorted = ranges.map((range, index) => ({ ...range, index })).sort((a, b) => a.rangeStart - b.rangeStart);
  for (let index = 1; index < sorted.length; index += 1) {
    if (sorted[index].rangeStart < sorted[index - 1].rangeEnd) {
      return {
        valid: false,
        totalPct: ranges.reduce((sum, range) => sum + range.probabilityWeightPct, 0),
        error: `Ranges ${sorted[index - 1].index + 1} and ${sorted[index].index + 1} overlap.`,
      };
    }
  }

  const totalPct = ranges.reduce((sum, range) => sum + range.probabilityWeightPct, 0);
  if (Math.abs(totalPct - 100) > tolerance) {
    return { valid: false, totalPct, error: `Population percentages must total 100% (currently ${totalPct}%).` };
  }

  return {
    valid: true,
    totalPct,
    error: "",
    minDepartureTime: Math.min(...ranges.map((range) => range.rangeStart)),
    maxDepartureTime: Math.max(...ranges.map((range) => range.rangeEnd)),
    ranges,
  };
}

export function createInitialDepartureRange(minRaw, maxRaw) {
  const rangeStart = Math.max(0, Math.trunc(Number(minRaw) || 0));
  const maxCandidate = Math.max(0, Math.trunc(Number(maxRaw) || 0));
  return {
    rangeStart,
    rangeEnd: maxCandidate > rangeStart ? maxCandidate : rangeStart + 5,
    probabilityWeightPct: 100,
  };
}

export function parseDepartureTimeConfig(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const minCandidate = Number(raw.min_departure_time ?? raw.minDepartureTime);
  const minDepartureTime = Number.isFinite(minCandidate) ? Math.max(0, Math.trunc(minCandidate)) : 0;
  const maxCandidate = Number(raw.max_departure_time ?? raw.maxDepartureTime);
  const maxDepartureTime = Number.isFinite(maxCandidate)
    ? Math.max(minDepartureTime, Math.trunc(maxCandidate))
    : minDepartureTime;
  return {
    minDepartureTime,
    maxDepartureTime,
    granularSurvey: Boolean(raw.granular_survey ?? raw.granularSurvey),
    startTimeRanges: normalizeStartTimeRanges(raw.start_time_ranges ?? raw.startTimeRanges),
  };
}

export function serializeDepartureTimeConfig(config) {
  const granularSurvey = Boolean(config?.granularSurvey);
  if (granularSurvey) {
    const validation = validateStartTimeRanges(config?.startTimeRanges);
    if (!validation.valid) throw new Error(validation.error);
    return {
      min_departure_time: validation.minDepartureTime,
      max_departure_time: validation.maxDepartureTime,
      granular_survey: true,
      start_time_ranges: validation.ranges.map((range) => ({
        range_start: range.rangeStart,
        range_end: range.rangeEnd,
        prob_weight: range.probabilityWeightPct / 100,
      })),
    };
  }

  const minCandidate = Number(config?.minDepartureTime);
  const minDepartureTime = Number.isFinite(minCandidate) ? Math.max(0, Math.trunc(minCandidate)) : 0;
  const maxCandidate = Number(config?.maxDepartureTime);
  const maxDepartureTime = Number.isFinite(maxCandidate)
    ? Math.max(minDepartureTime, Math.trunc(maxCandidate))
    : minDepartureTime;
  return {
    min_departure_time: minDepartureTime,
    max_departure_time: maxDepartureTime,
    granular_survey: false,
  };
}
